import type { VideoMeta } from './types';

export const MAX_PLAYING_VIDEOS = 3;
export const VISIBILITY_THRESHOLD = 0.5;

export function getVisibleVideos(videos: Map<string, VideoMeta>): VideoMeta[] {
  return Array.from(videos.values())
    .filter((v) => v.visibility >= VISIBILITY_THRESHOLD)
    .sort((a, b) => a.index - b.index);
}

function findClosestToMidIndex(
  videos: VideoMeta[],
  midIndex: number,
  exclude: Set<string>
): VideoMeta | null {
  return videos
    .filter((v) => !exclude.has(v.id))
    .reduce<VideoMeta | null>((closest, v) => {
      const distance = Math.abs(v.index - midIndex);

      return !closest || distance < Math.abs(closest.index - midIndex)
        ? v
        : closest;
    }, null);
}

export function selectVideosToPlay(
  visible: VideoMeta[],
  chosenVideoIds: Set<string>,
  videos: Map<string, VideoMeta>
): Set<string> {
  const newChosenIds = new Set(chosenVideoIds);

  if (visible.length === 0) {
    return newChosenIds;
  }

  const [first] = visible;
  const last = visible[visible.length - 1];

  if (newChosenIds.size < MAX_PLAYING_VIDEOS && !first.wasCalculated) {
    newChosenIds.add(first.id);
  }

  if (
    last.id !== first.id &&
    newChosenIds.size < MAX_PLAYING_VIDEOS &&
    !last.wasCalculated
  ) {
    newChosenIds.add(last.id);
  }

  if (visible.length > 2 && newChosenIds.size < MAX_PLAYING_VIDEOS) {
    const chosenVideos = Array.from(newChosenIds)
      .map((id) => videos.get(id))
      .filter((v): v is VideoMeta => v !== undefined);

    if (chosenVideos.length >= 2) {
      const [firstChosen, lastChosen] = [
        chosenVideos[0],
        chosenVideos[chosenVideos.length - 1]
      ];
      const midIndex = Math.round((firstChosen.index + lastChosen.index) / 2);
      const excludeIds = new Set([firstChosen.id, lastChosen.id]);
      const mid = findClosestToMidIndex(visible, midIndex, excludeIds);

      if (mid && !mid.wasCalculated) {
        newChosenIds.add(mid.id);
      }
    }
  }

  return newChosenIds;
}
